import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useSession,
  useSessionUsers,
  useVideoState,
  useAudioState,
  VideoPlayerContainerComponent,
  VideoPlayerComponent,
  useMyself
} from '@zoom/videosdk-react';
import ZoomVideo, { LiveTranscriptionLanguage, TranscriptionMode } from '@zoom/videosdk';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Maximize,
  Users,
  PhoneOff,
  Settings,
  Subtitles,
  Loader2
} from 'lucide-react';
import Header from '../components/Header';
import { getToken, getUserId } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { zoomJoinErrorMessage } from '../utils/appointmentHelpers';
import { checkZoomJoinEligibility } from '../utils/zoomSession';
import { useToast } from '../context/ToastContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/** Speaking languages supported by Zoom Video SDK live transcription (session mode). */
const LIVE_TRANSCRIPTION_LANGUAGES = [
  { label: 'English', value: LiveTranscriptionLanguage.English },
  { label: 'Spanish', value: LiveTranscriptionLanguage.Spanish },
  { label: 'French', value: LiveTranscriptionLanguage.French },
  { label: 'German', value: LiveTranscriptionLanguage.German },
  { label: 'Italian', value: LiveTranscriptionLanguage.Italian },
  { label: 'Portuguese', value: LiveTranscriptionLanguage.Portuguese },
  { label: 'Dutch', value: LiveTranscriptionLanguage.Dutch },
  { label: 'Russian', value: LiveTranscriptionLanguage.Russian },
  { label: 'Japanese', value: LiveTranscriptionLanguage.Japanese },
  { label: 'Korean', value: LiveTranscriptionLanguage.Korean },
  { label: 'Arabic', value: LiveTranscriptionLanguage.Arabic },
  { label: 'Hindi', value: LiveTranscriptionLanguage.Hindi },
  { label: 'Chinese (Simplified)', value: LiveTranscriptionLanguage['Chinese (Simplified)'] },
  { label: 'Chinese (Traditional)', value: LiveTranscriptionLanguage['Chinese (Traditional)'] },
  { label: 'Vietnamese', value: LiveTranscriptionLanguage.Vietnamese },
  { label: 'Turkish', value: LiveTranscriptionLanguage.Turkish },
  { label: 'Polish', value: LiveTranscriptionLanguage.Polish },
  { label: 'Swedish', value: LiveTranscriptionLanguage.Swedish },
  { label: 'Indonesian', value: LiveTranscriptionLanguage.Indonesian },
  { label: 'Thai', value: LiveTranscriptionLanguage.Thai },
  { label: 'Hebrew', value: LiveTranscriptionLanguage.Hebrew },
  { label: 'Greek', value: LiveTranscriptionLanguage.Greek },
  { label: 'Norwegian', value: LiveTranscriptionLanguage.Norwegian },
  { label: 'Finnish', value: LiveTranscriptionLanguage.Finnish },
  { label: 'Czech', value: LiveTranscriptionLanguage.Czech },
  { label: 'Romanian', value: LiveTranscriptionLanguage.Romanian },
  { label: 'Ukrainian', value: LiveTranscriptionLanguage.Ukrainian },
  { label: 'Danish', value: LiveTranscriptionLanguage.Danish },
  { label: 'Hungarian', value: LiveTranscriptionLanguage.Hungarian },
  { label: 'French (Canada)', value: LiveTranscriptionLanguage['French (Canada)'] }
];

const buildApiUrl = (path, hasId = false) => {
  const base = (API_BASE_URL || '').replace(/\/+$/, '');
  const p = (path || '').replace(/^\/+|\/+$/g, '');
  return hasId ? `${base}/${p}` : `${base}/${p}/`;
};

const isZoomPushType = (payload) => {
  const t = String(payload?.notificationType || '').toLowerCase();
  return (
    t === 'video_waiting_room' ||
    t === 'video_patient_left_call' ||
    t.startsWith('zoom_')
  );
};

const UpcomingAppointmentCard = ({ sessionName, userName, isJoining, onPatientMrn }) => {
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
    const lastZoomRefreshKeyRef = useRef('');

  const loadUpcomingAppointment = useCallback(() => {
    let cancelled = false;
    const token = getToken();
    if (!token) {
      setLoading(false);
      return () => {};
    }
    fetch(buildApiUrl('appointments/with-relations', false), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        const withSession = data.find(
          (a) => a.meeting_url && String(a.meeting_url).trim() && String(a.meeting_url).trim() === String(sessionName).trim()
        );
        if (withSession) {
          setAppointment(withSession);
          return;
        }
        const upcoming = data
          .filter((a) => a.startTime && new Date(a.startTime) > new Date())
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        setAppointment(upcoming[0] || null);
      })
      .catch(() => setAppointment(null))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionName]);

  useEffect(() => {
    const cleanup = loadUpcomingAppointment();
    return cleanup;
  }, [loadUpcomingAppointment]);

  useEffect(() => {
    const refresh = () => loadUpcomingAppointment();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    let removeSwListener = () => {};
    if ('serviceWorker' in navigator) {
      const onSwMessage = (event) => {
        if (event?.data?.type !== 'WEB_PUSH_RECEIVED') return;
        const pushPayload = event?.data?.payload || {};
        if (!isZoomPushType(pushPayload)) return;
        const key = `${String(pushPayload?.sessionName || pushPayload?.session_name || '')}:${String(pushPayload?.notificationType || '')}`;
        if (lastZoomRefreshKeyRef.current === key) return;
        lastZoomRefreshKeyRef.current = key;
        refresh();
      };
      navigator.serviceWorker.addEventListener('message', onSwMessage);
      removeSwListener = () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
    }
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      removeSwListener();
    };
  }, [loadUpcomingAppointment]);

  const displayDate = appointment?.startTime
    ? new Date(appointment.startTime).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : null;
  const displayTime = appointment?.startTime
    ? new Date(appointment.startTime).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="relative flex-1 rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden p-6 flex flex-col min-h-0">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Upcoming appointment</p>
      {loading ? (
        <div className="flex-1 flex items-center justify-center min-h-[180px]">
          <p className="text-sm text-gray-500">Loading…</p>
        </div>
      ) : appointment ? (
        <>
          <div className="space-y-1 mb-4">
            <p className="text-base font-semibold text-gray-900">
              {appointment.patient_name || appointment.title || 'Consultation'}
            </p>
            {(displayDate || displayTime) && (
              <p className="text-sm text-gray-600">
                {displayDate}
                {displayTime ? ` · ${displayTime}` : ''}
              </p>
            )}
            {appointment.appointmentType && (
              <span className="inline-block mt-1 rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs font-medium">
                {appointment.appointmentType}
              </span>
            )}
          </div>
          <div className="flex-1 flex items-center justify-center min-h-[120px]">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-semibold text-lg mb-2">
                {(appointment.patient_name || 'P').charAt(0).toUpperCase()}
              </div>
              <p className="text-sm font-medium text-gray-900 truncate max-w-[10rem]">
                {appointment.patient_name || 'Patient'}
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center min-h-[180px]">
          <div className="text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-xl font-semibold mb-3">
              {userName.charAt(0).toUpperCase()}
            </div>
            <p className="text-sm font-medium text-gray-900 truncate max-w-[10rem]">{userName || 'Clinician'}</p>
            <p className="text-xs text-gray-500 mt-1">Camera and mic ready after you join</p>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-500 mt-4">Press V to toggle video · M to mute</p>
    </div>
  );
};

const OnlineConsultationPage = () => {
  const location = useLocation();
  const { user } = useAuth();

  const urlParams = new URLSearchParams(location.search);
  const sessionNameFromUrl = urlParams.get('session');
  const patientMrnFromUrl = urlParams.get('patientMrn');
  const parsedPatientMrnFromUrl =
    patientMrnFromUrl != null && patientMrnFromUrl !== ''
      ? parseInt(patientMrnFromUrl, 10)
      : null;
  const initialLinkedMrn = Number.isFinite(parsedPatientMrnFromUrl) ? parsedPatientMrnFromUrl : null;

  const generateSessionName = () => {
    const timestamp = Date.now();
    return `consult-${timestamp}`;
  };

  const getUserName = () => {
    if (user?.sub) {
      const username = user.sub;
      if (username.startsWith('clinician-')) {
        return `Dr. ${username.replace('clinician-', '').replace(/[_-]/g, ' ')}`;
      } else if (username.startsWith('patient-')) {
        return username.replace('patient-', '').replace(/[_-]/g, ' ');
      } else if (username === 'admin') {
        return 'Administrator';
      }
      return username;
    }
    return `User-${getUserId() || Math.floor(Math.random() * 1000)}`;
  };

  const [sessionName, setSessionName] = useState(sessionNameFromUrl || generateSessionName());
  const [userName, setUserName] = useState(getUserName());
  const [zoomToken, setZoomToken] = useState('');
  const [sessionPassword, setSessionPassword] = useState('');
  const [callStartedAtMs, setCallStartedAtMs] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [mediaPermission, setMediaPermission] = useState({
    camera: 'unknown',
    microphone: 'unknown',
    checked: false,
    error: '',
  });
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userMRN, setUserMRN] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showWaitingRoomInfo, setShowWaitingRoomInfo] = useState(true);
  const [linkedPatientMrn, setLinkedPatientMrn] = useState(initialLinkedMrn);
  const { showToast } = useToast();
  const autoJoinKeyRef = useRef('');

  const refreshMediaPermissions = useCallback(async () => {
    let cameraState = 'unknown';
    let microphoneState = 'unknown';
    try {
      if (navigator?.permissions?.query) {
        try {
          const cameraPerm = await navigator.permissions.query({ name: 'camera' });
          cameraState = cameraPerm.state || 'unknown';
        } catch {}
        try {
          const micPerm = await navigator.permissions.query({ name: 'microphone' });
          microphoneState = micPerm.state || 'unknown';
        } catch {}
      }
    } catch {}
    setMediaPermission((prev) => ({
      ...prev,
      camera: cameraState,
      microphone: microphoneState,
      checked: true,
    }));
  }, []);

  const ensureMediaPermissions = useCallback(async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setMediaPermission((prev) => ({
        ...prev,
        error: 'Camera/microphone access is not supported in this browser.',
        checked: true,
      }));
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach((track) => track.stop());
      await refreshMediaPermissions();
      setMediaPermission((prev) => ({ ...prev, error: '' }));
      return true;
    } catch {
      await refreshMediaPermissions();
      setMediaPermission((prev) => ({
        ...prev,
        error: 'Camera or microphone permission is blocked. Allow both in browser site settings.',
      }));
      return false;
    }
  }, [refreshMediaPermissions]);

  useEffect(() => {
    refreshMediaPermissions();
  }, [refreshMediaPermissions]);

  const getZoomToken = useCallback(async ({ skipEligibility = false } = {}) => {
    try {
      setIsJoining(true);
      setTokenError('');

      const token = getToken();
      if (!token) {
        setTokenError('You must be logged in to join a video session');
        return false;
      }

      if (!skipEligibility) {
        const eligibility = await checkZoomJoinEligibility({
          apiBaseUrl: API_BASE_URL,
          token,
          sessionName,
          patientMrn: linkedPatientMrn,
        });
        if (!eligibility.ok) {
          setTokenError(eligibility.reason);
          showToast(eligibility.reason, 'error');
          return false;
        }
      }

      const hasMediaPermissions = await ensureMediaPermissions();
      if (!hasMediaPermissions) {
        const msg = 'Please allow camera and microphone access before joining.';
        setTokenError(msg);
        showToast(msg, 'error');
        return false;
      }

      const tokenBody = {
        sessionName: sessionName,
        role: 'Doctor',
        userIdentity: userName,
        sessionKey: null,
        expirationSeconds: 7200
      };
      if (linkedPatientMrn != null) {
        tokenBody.patientMrn = linkedPatientMrn;
      }

      const response = await axios.post(`${API_BASE_URL}/zoom-video/token`, tokenBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const jwtToken = response.data.token;
      setZoomToken(jwtToken);
      setSessionPassword(response.data.sessionPassword || '');
      setUserDisplayName(response.data.displayName || userName);
      setUserMRN(response.data.mrn || null);
      const startedMs = response.data.callStartedAt
        ? Date.parse(response.data.callStartedAt)
        : Date.now();
      const serverNowMs = response.data.serverNow
        ? Date.parse(response.data.serverNow)
        : Date.now();
      const clockSkewMs = Number.isFinite(serverNowMs) ? Date.now() - serverNowMs : 0;
      setCallStartedAtMs(
        Number.isFinite(startedMs) ? startedMs + clockSkewMs : Date.now()
      );
      setHasJoined(true);
      return true;
    } catch (err) {
      let errorMessage = 'Failed to get video session token. ';
      const detail = err.response?.data?.detail;

      if (err.response?.status === 500) {
        errorMessage +=
          'Zoom SDK credentials are not configured in backend .env file. Please add ZOOM_VIDEO_SDK_KEY and ZOOM_VIDEO_SDK_SECRET.';
      } else if (err.response?.status === 401) {
        errorMessage += 'Your session has expired. Please log out and log back in.';
      } else if (err.response?.status === 403 && detail) {
        errorMessage = zoomJoinErrorMessage(detail);
      } else if (detail) {
        errorMessage += typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Unknown error occurred. Check backend logs for details.';
      }

      setTokenError(errorMessage);
      showToast(errorMessage, 'error');
      return false;
    } finally {
      setIsJoining(false);
    }
  }, [sessionName, userName, linkedPatientMrn, ensureMediaPermissions, showToast]);

  useEffect(() => {
    if (!sessionNameFromUrl || hasJoined || isJoining) return;
    const key = `${sessionNameFromUrl}:${linkedPatientMrn ?? ''}`;
    if (autoJoinKeyRef.current === key) return;
    autoJoinKeyRef.current = key;
    getZoomToken();
  }, [sessionNameFromUrl, linkedPatientMrn, hasJoined, isJoining, getZoomToken]);

  const handleLeaveSession = async () => {
    if (hasJoined && sessionName && userName) {
      try {
        const token = getToken();
        await axios.post(
          `${API_BASE_URL}/zoom-video/session/${sessionName}/leave`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } catch {}
    }
    autoJoinKeyRef.current = '';
    setHasJoined(false);
    setZoomToken('');
    setCallStartedAtMs(null);
    setIsJoining(false);
    setTokenError('');
  };

  const handleManualJoin = useCallback(() => {
    autoJoinKeyRef.current = `${sessionName}:${linkedPatientMrn ?? ''}`;
    return getZoomToken();
  }, [getZoomToken, sessionName, linkedPatientMrn]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(sessionName);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere text-gray-900">
      <Header />
      <div className="flex-1 flex flex-col">
        {hasJoined && zoomToken ? (
          <VideoConsultationShell
            sessionName={sessionName}
            userName={userName}
            token={zoomToken}
            sessionPassword={sessionPassword}
            onLeave={handleLeaveSession}
            displayName={userDisplayName}
            userMRN={userMRN}
            callStartedAtMs={callStartedAtMs}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center px-4 py-6 md:px-8">
            <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="space-y-2">
                  <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">
                    Video consultation
                  </h1>
                  <p className="text-sm text-gray-600">
                    HIPAA-ready video powered by Zoom. Patients join from their browser.
                  </p>
                </div>
                <div className="relative rounded-2xl bg-white border border-gray-200 shadow-lg p-6 space-y-5">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Session ID</label>
                      <span className="inline-flex h-5 items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs whitespace-nowrap">
                        <span className="text-blue-700/70">Powered by</span>
                        <img src="/Zoom_Logo_Bloom.svg" alt="Zoom" className="h-3 w-3" />
                        <span className="text-blue-700 font-medium">Zoom</span>
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-500 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all"
                        placeholder="consult-session-id"
                        disabled={isJoining || !!sessionNameFromUrl}
                      />
                      <button
                        onClick={copyToClipboard}
                        type="button"
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 text-gray-700 hover:bg-gray-50 transition-colors text-xs font-medium"
                        title="Copy session ID"
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    {sessionNameFromUrl && (
                      <p className="text-xs text-gray-500">Locked to this appointment</p>
                    )}
                  </div>
                  <div className="space-y-3">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Display name</label>
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-500 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all"
                      placeholder="Dr. Jane Doe"
                      disabled={isJoining}
                    />
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Permissions</p>
                      <button
                        type="button"
                        onClick={ensureMediaPermissions}
                        disabled={isJoining}
                        className="text-xs text-teal-700 hover:text-teal-600 disabled:text-gray-400"
                      >
                        Check access
                      </button>
                    </div>
                    <p className="text-xs text-gray-600">
                      Camera: {mediaPermission.camera} · Microphone: {mediaPermission.microphone}
                    </p>
                    {mediaPermission.error && (
                      <p className="text-xs text-red-700">{mediaPermission.error}</p>
                    )}
                  </div>
                  {tokenError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-1.5">
                      <p className="font-semibold flex items-center gap-1.5">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
                        Connection issue
                      </p>
                      <p className="text-xs leading-relaxed">{tokenError}</p>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleManualJoin}
                    disabled={
                      isJoining ||
                      !sessionName ||
                      !userName ||
                      mediaPermission.camera === 'denied' ||
                      mediaPermission.microphone === 'denied'
                    }
                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-500 px-4 py-3 text-sm font-semibold text-white shadow-md disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-all"
                  >
                    <Video className="w-5 h-5 shrink-0" />
                    <span>{isJoining ? 'Connecting…' : tokenError ? 'Try again' : 'Join video consultation'}</span>
                  </button>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-gray-500">Typical visit 15–25 min</span>
                    <button
                      type="button"
                      onClick={() => setShowWaitingRoomInfo((prev) => !prev)}
                      className="text-xs text-gray-500 hover:text-teal-600 transition-colors"
                    >
                      {showWaitingRoomInfo ? 'Hide info' : 'Waiting room info'}
                    </button>
                  </div>
                  {showWaitingRoomInfo && (
                    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-2">
                      <p className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                        Patients see a waiting room while they connect.
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        You&apos;ll see the patient as soon as they join.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="hidden lg:flex flex-col">
                <UpcomingAppointmentCard
                  sessionName={sessionName}
                  userName={userName}
                  isJoining={isJoining}
                  onPatientMrn={setLinkedPatientMrn}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const VideoConsultationShell = ({ sessionName, userName, token, sessionPassword, onLeave, displayName, userMRN, linkedPatientMrn, callStartedAtMs }) => {
  const {
    isInSession,
    isLoading,
    isError,
    error: sessionError
  } = useSession(sessionName, token, userName, sessionPassword || undefined, undefined, {
    disableVideo: false,
    disableAudio: false,
    videoOptions: { originalRatio: true },
    language: 'en-US',
    dependentAssets: 'Global'
  });

  const users = useSessionUsers();
  const myself = useMyself();
  const { isVideoOn, toggleVideo, setVideo } = useVideoState();
  const { isAudioMuted, toggleMute } = useAudioState();

  const [dominantSpeakerId] = useState(null);
  const [pinnedUserId, setPinnedUserId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(true);
  const [isHost, setIsHost] = useState(false);
  const [selectedTranscriptionLang, setSelectedTranscriptionLang] = useState(
    LiveTranscriptionLanguage.English
  );
  /** Host explicitly started transcription in Settings — never auto-true from Zoom SDK defaults. */
  const [hostTranscriptionStarted, setHostTranscriptionStarted] = useState(false);
  const [transcriptionBusy, setTranscriptionBusy] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [raisedHands] = useState({});
  const [networkQuality] = useState(4);
  const [videoResolutionMode, setVideoResolutionMode] = useState('auto');
  const [videoAspectMode, setVideoAspectMode] = useState('original');
  const [showVideoSettings, setShowVideoSettings] = useState(false);
  const [mirrorVideo, setMirrorVideo] = useState(true);
  const [, setTick] = useState(0);
  const toggleVideoSettings = useCallback(() => setShowVideoSettings((prev) => !prev), []);
  const closeVideoSettings = useCallback(() => setShowVideoSettings(false), []);

  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const flushArtifactsToBackend = useCallback(async () => {
    const auth = getToken();
    if (!auth) return;
    const client = ZoomVideo.createClient();
    if (!client.getSessionInfo().isInMeeting) return;

    const base = `${API_BASE_URL}/zoom-video/session/${encodeURIComponent(sessionName)}`;

    try {
      const lt = client.getLiveTranscriptionClient();
      const hist = lt.getFullTranscriptionHistory();
      const arr = await Promise.resolve(hist);
      const list = Array.isArray(arr) ? arr : [];
      const fullText = list
        .map((m) => {
          const ts = typeof m.timestamp === 'number' ? new Date(m.timestamp).toISOString() : '';
          return `[${ts}] ${m.displayName || ''}: ${m.text || ''}`;
        })
        .join('\n');
      if (fullText.trim()) {
        let segments = list;
        try {
          segments = JSON.parse(JSON.stringify(list));
        } catch {
          segments = null;
        }
        await axios.post(
          `${base}/transcript`,
          { fullText, segments },
          {
            headers: {
              Authorization: `Bearer ${auth}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }
    } catch {}

  }, [sessionName]);

  const handleLeaveWithFlush = useCallback(async () => {
    await flushArtifactsToBackend();
    onLeave();
  }, [flushArtifactsToBackend, onLeave]);

  useEffect(() => {
    if (!isInSession || isLoading) return undefined;
    const client = ZoomVideo.createClient();

    const syncHost = () => {
      try {
        setIsHost(!!client.isHost?.());
      } catch {
        setIsHost(false);
      }
    };

    const syncTranscriptionStoppedOnly = () => {
      try {
        if (!client.getSessionInfo().isInMeeting) return;
        const lt = client.getLiveTranscriptionClient();
        const st = lt.getLiveTranscriptionStatus();
        if (!st) return;
        const sdkOn = !!st.isLiveTranscriptionEnabled && !st.isHostDisableCaptions;
        if (!sdkOn) {
          setHostTranscriptionStarted(false);
        }
      } catch {
        /* ignore */
      }
    };

    const onCaptionEnable = (enabled) => {
      if (!enabled) setHostTranscriptionStarted(false);
    };

    const onCaptionHostDisable = (captionsDisabledByHost) => {
      if (captionsDisabledByHost) setHostTranscriptionStarted(false);
    };

    const onCaptionStatus = () => {
      syncTranscriptionStoppedOnly();
    };

    syncHost();
    client.on('caption-enable', onCaptionEnable);
    client.on('caption-host-disable', onCaptionHostDisable);
    client.on('caption-status', onCaptionStatus);

    const poll = () => {
      try {
        if (!client.getSessionInfo().isInMeeting) return;
        syncHost();
        syncTranscriptionStoppedOnly();
      } catch {
        /* ignore */
      }
    };
    poll();
    const interval = window.setInterval(poll, 4000);

    return () => {
      client.off('caption-enable', onCaptionEnable);
      client.off('caption-host-disable', onCaptionHostDisable);
      client.off('caption-status', onCaptionStatus);
      clearInterval(interval);
    };
  }, [isInSession, isLoading]);

  /** Zoom may enable captions/transcription by default; host-only: force off until Settings → Start. */
  const transcriptionJoinResetDoneRef = useRef(false);
  useEffect(() => {
    if (!isInSession || isLoading || !isHost) return;
    if (transcriptionJoinResetDoneRef.current) return;
    const timer = window.setTimeout(() => {
      try {
        const client = ZoomVideo.createClient();
        if (!client.getSessionInfo().isInMeeting) return;
        if (!client.isHost?.()) return;
        transcriptionJoinResetDoneRef.current = true;
        const lt = client.getLiveTranscriptionClient();
        lt.disableCaptions(true);
        setHostTranscriptionStarted(false);
      } catch {}
    }, 500);
    return () => clearTimeout(timer);
  }, [isInSession, isLoading, isHost]);

  useEffect(() => {
    if (!showVideoSettings || !isInSession || isLoading) return;
    const client = ZoomVideo.createClient();
    try {
      if (!client.getSessionInfo().isInMeeting) return;
      setIsHost(!!client.isHost?.());
    } catch {
      /* ignore */
    }
  }, [showVideoSettings, isInSession, isLoading]);

  const handleTranscriptionLanguageChange = useCallback(
    async (valueString) => {
      const lang = LIVE_TRANSCRIPTION_LANGUAGES.find((x) => x.value === valueString)?.value;
      if (!lang) return;
      setSelectedTranscriptionLang(lang);
      if (!hostTranscriptionStarted || !isHost) return;
      setTranscriptionBusy(true);
      setTranscriptionError(null);
      try {
        const client = ZoomVideo.createClient();
        const lt = client.getLiveTranscriptionClient();
        const r = await lt.setSpeakingLanguage(lang, { mode: TranscriptionMode.Session });
        if (r !== '') throw new Error(r.reason || 'Could not update language');
      } catch (e) {
        setTranscriptionError(e.message || 'Language update failed');
      } finally {
        setTranscriptionBusy(false);
      }
    },
    [hostTranscriptionStarted, isHost]
  );

  const handleStartLiveTranscription = useCallback(async () => {
    const client = ZoomVideo.createClient();
    if (!client.getSessionInfo().isInMeeting) {
      setTranscriptionError('You must be in the call to start transcription.');
      return;
    }
    if (!client.isHost()) {
      setTranscriptionError('Only the host can start live transcription.');
      return;
    }
    setTranscriptionBusy(true);
    setTranscriptionError(null);
    try {
      const lt = client.getLiveTranscriptionClient();
      // Captions must be allowed before ASR starts (Zoom sample: disableCaptions blocks start).
      let r = await lt.disableCaptions(false);
      r = await lt.startLiveTranscription();
      if (r !== '') throw new Error(r.reason || 'Could not start live transcription');
      r = await lt.setSpeakingLanguage(selectedTranscriptionLang, { mode: TranscriptionMode.Session });
      if (r !== '') throw new Error(r.reason || 'Could not apply language');
      setHostTranscriptionStarted(true);
    } catch (e) {
      setTranscriptionError(e.message || 'Failed to start transcription');
    } finally {
      setTranscriptionBusy(false);
    }
  }, [selectedTranscriptionLang]);

  const handleStopLiveTranscription = useCallback(async () => {
    const client = ZoomVideo.createClient();
    if (!client.getSessionInfo().isInMeeting) {
      setTranscriptionError('Not in a meeting.');
      return;
    }
    if (!client.isHost()) {
      setTranscriptionError('Only the host can end transcription.');
      return;
    }
    setTranscriptionBusy(true);
    setTranscriptionError(null);
    try {
      const lt = client.getLiveTranscriptionClient();
      const r = await lt.disableCaptions(true);
      if (r !== '') throw new Error(r.reason || 'Could not end transcription');
      setHostTranscriptionStarted(false);
      for (let i = 0; i < 15; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
        const s = lt.getLiveTranscriptionStatus();
        if (!s?.isLiveTranscriptionEnabled || s?.isHostDisableCaptions) {
          setHostTranscriptionStarted(false);
          break;
        }
      }
    } catch (e) {
      setTranscriptionError(e.message || 'Failed to end transcription');
    } finally {
      setTranscriptionBusy(false);
    }
  }, []);

  const participants = useMemo(() => users || [], [users]);
  const selfUser = useMemo(
    () => participants.find((u) => u.userId === myself?.userId),
    [participants, myself]
  );
  const remoteParticipants = useMemo(
    () => participants.filter((u) => u.userId !== myself?.userId),
    [participants, myself]
  );

  const effectiveDominantUser = useMemo(() => {
    if (pinnedUserId) {
      return participants.find((u) => u.userId === pinnedUserId) || selfUser || participants[0];
    }
    if (dominantSpeakerId) {
      return participants.find((u) => u.userId === dominantSpeakerId) || selfUser || participants[0];
    }
    if (remoteParticipants.length > 0) return remoteParticipants[0];
    return selfUser || participants[0];
  }, [participants, selfUser, remoteParticipants, dominantSpeakerId, pinnedUserId]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(() => undefined);
    } else {
      document.exitFullscreen().catch(() => undefined);
    }
  };

  const videoCaptureOptions = useMemo(() => {
    const opts = {
      originalRatio: videoAspectMode === 'original',
      mirrored: mirrorVideo
    };
    if (videoResolutionMode === '720p') {
      opts.hd = true;
      opts.fullHd = false;
    } else if (videoResolutionMode === '1080p') {
      opts.hd = true;
      opts.fullHd = true;
    }
    return opts;
  }, [videoResolutionMode, videoAspectMode, mirrorVideo]);

  // Reapply capture options when resolution / mirror / aspect change while video is already on.
  // Do NOT force video on when the user has turned it off (that was causing camera to snap back on).
  useEffect(() => {
    if (!isVideoOn) return;
    setVideo(true, videoCaptureOptions).catch(() => undefined);
  }, [isVideoOn, setVideo, videoCaptureOptions]);

  const handleToggleVideo = useCallback(() => {
    if (isVideoOn) return toggleVideo();
    return toggleVideo(videoCaptureOptions);
  }, [isVideoOn, toggleVideo, videoCaptureOptions]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'v' || e.key === 'V') handleToggleVideo();
      if (e.key === 'm' || e.key === 'M') toggleMute();
      if (e.key === 'f' || e.key === 'F') toggleFullscreen();
      if (e.key === 'q' || e.key === 'Q') handleLeaveWithFlush();
      if (e.key === 'p' || e.key === 'P') setShowParticipantsPanel((prev) => !prev);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleToggleVideo, toggleMute, handleLeaveWithFlush]);

  const handleMouseMove = () => {
    if (showVideoSettings) return;
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      window.clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = window.setTimeout(() => {
      setShowControls(false);
    }, 3200);
  };

  /** Hide bottom controls while settings modal is open; restore when it closes. */
  const wasSettingsOpenRef = useRef(false);
  useEffect(() => {
    if (showVideoSettings) {
      wasSettingsOpenRef.current = true;
      if (controlsTimeoutRef.current) {
        window.clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = null;
      }
    } else if (wasSettingsOpenRef.current) {
      wasSettingsOpenRef.current = false;
      setShowControls(true);
    }
  }, [showVideoSettings]);

  useEffect(
    () => () => {
      if (controlsTimeoutRef.current) {
        window.clearTimeout(controlsTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleTogglePin = (userId) => {
    setPinnedUserId((prev) => (prev === userId ? null : userId));
  };

  const startedAt = callStartedAtMs || Date.now();
  const uptimeMs = Math.max(0, Date.now() - startedAt);
  const minutes = Math.floor(uptimeMs / 60000);
  const seconds = Math.floor((uptimeMs % 60000) / 1000)
    .toString()
    .padStart(2, '0');

  if (isLoading || !isInSession) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-full border-2 border-gray-200 border-t-teal-500 animate-spin" />
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-gray-900">Connecting to consultation…</p>
            <p className="text-xs text-gray-600">Please keep this tab open.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100">
        <div className="max-w-sm rounded-xl border border-red-200 bg-white shadow-lg px-5 py-4 space-y-2">
          <p className="text-sm font-semibold text-gray-900">Unable to start consultation</p>
          <p className="text-xs text-gray-600">
            {sessionError?.reason || 'An unexpected error occurred while joining the video session.'}
          </p>
          <button
            type="button"
            onClick={handleLeaveWithFlush}
            className="mt-2 inline-flex items-center justify-center rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
          >
            Return
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative flex-1 flex flex-col min-h-0 bg-gray-100"
      onMouseMove={handleMouseMove}
    >
      <TelehealthHeader
        sessionName={sessionName}
        displayName={displayName}
        userMRN={userMRN}
        linkedPatientMrn={linkedPatientMrn}
        networkQuality={networkQuality}
        callDuration={`${minutes}:${seconds}`}
      />
      <div className="flex-1 flex overflow-hidden min-h-0">
        <VideoLayout
          participants={participants}
          selfUser={selfUser}
          dominantUser={effectiveDominantUser}
          remoteParticipants={remoteParticipants}
          pinnedUserId={pinnedUserId}
          onPin={handleTogglePin}
          raisedHands={raisedHands}
        />
        <ParticipantsPanel
          open={showParticipantsPanel}
          participants={participants}
          selfUser={selfUser}
          raisedHands={raisedHands}
          networkQuality={networkQuality}
          onToggleMuteParticipant={() => undefined}
        />

        {showVideoSettings && (
          <div className="absolute inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="w-full max-w-lg max-h-[min(90vh,720px)] bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4 text-gray-600" />
                  <p className="text-sm font-semibold text-gray-900">Session &amp; video settings</p>
                </div>
                <button
                  type="button"
                  onClick={closeVideoSettings}
                  className="inline-flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700"
                >
                  Close
                </button>
              </div>

              <div className="px-5 py-4 space-y-6 overflow-y-auto flex-1 min-h-0">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-teal-700 mb-3">Camera</p>
                  <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Resolution</p>
                  <div className="flex flex-wrap gap-2">
                    {['auto', '720p', '1080p'].map((opt) => {
                      const selected = videoResolutionMode === opt;
                      const label = opt === 'auto' ? 'Auto' : opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setVideoResolutionMode(opt)}
                          className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                            selected
                              ? 'bg-teal-50 border-teal-200 text-teal-700'
                              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Aspect ratio</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setVideoAspectMode('original')}
                      className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                        videoAspectMode === 'original'
                          ? 'bg-teal-50 border-teal-200 text-teal-700'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Original
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoAspectMode('fit')}
                      className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                        videoAspectMode === 'fit'
                          ? 'bg-teal-50 border-teal-200 text-teal-700'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Fit 16:9
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Mirror</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setMirrorVideo(true)}
                      className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                        mirrorVideo ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      On
                    </button>
                    <button
                      type="button"
                      onClick={() => setMirrorVideo(false)}
                      className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                        !mirrorVideo ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>

                <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                  <p className="text-[12px] text-gray-600 leading-relaxed">
                    These options apply to <span className="font-medium text-gray-800">your local camera</span> only.
                  </p>
                </div>
                </div>

                <div className="border-t border-gray-100 pt-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-teal-700 mb-3">Live transcription</p>
                  <div className="rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50/80 via-white to-sky-50/90 p-4 shadow-sm">
                    <div className="flex gap-3 mb-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/20">
                        <Subtitles className="h-5 w-5" strokeWidth={2.25} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Session captions &amp; transcript</p>
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                          Choose the spoken language, then start. Session mode applies to everyone. The full transcript is saved when you leave the call.
                        </p>
                      </div>
                    </div>

                    {isHost ? (
                      <>
                        <label className="block">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Transcription language</span>
                          <div className="relative mt-1.5">
                            <select
                              value={selectedTranscriptionLang}
                              onChange={(e) => handleTranscriptionLanguageChange(e.target.value)}
                              disabled={transcriptionBusy}
                              className="w-full appearance-none rounded-xl border border-gray-200 bg-white pl-3 pr-9 py-2.5 text-sm font-medium text-gray-900 shadow-sm outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-200/80 disabled:opacity-60"
                            >
                              {LIVE_TRANSCRIPTION_LANGUAGES.map(({ label, value }) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                          </div>
                        </label>

                        <div className="mt-4 flex flex-col sm:flex-row gap-2">
                          {hostTranscriptionStarted ? (
                            <button
                              type="button"
                              onClick={handleStopLiveTranscription}
                              disabled={transcriptionBusy}
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-700 shadow-sm transition hover:bg-rose-50 disabled:opacity-50"
                            >
                              {transcriptionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                              End live transcription
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={handleStartLiveTranscription}
                              disabled={transcriptionBusy}
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-teal-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-600/25 transition hover:from-teal-500 hover:to-teal-500 disabled:opacity-50"
                            >
                              {transcriptionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                              Start live transcription
                            </button>
                          )}
                        </div>

                        {hostTranscriptionStarted ? (
                          <div className="mt-3 flex items-start gap-2 rounded-lg bg-teal-600/10 border border-teal-200/60 px-3 py-2 text-xs text-teal-900">
                            <span className="relative mt-1 flex h-2 w-2 shrink-0">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-60" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-500" />
                            </span>
                            <span className="font-medium leading-snug">Live transcription is running for this session.</span>
                          </div>
                        ) : null}

                        {transcriptionError ? (
                          <p className="mt-3 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{transcriptionError}</p>
                        ) : null}
                      </>
                    ) : (
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Only the <span className="font-medium text-gray-800">session host</span> can start or stop live transcription. Ask the host to open Settings and start it when ready.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ControlBar
        show={showControls && !showVideoSettings}
        isVideoOn={isVideoOn}
        isAudioMuted={isAudioMuted}
        isFullscreen={isFullscreen}
        onToggleVideo={handleToggleVideo}
        onToggleAudio={toggleMute}
        onToggleFullscreen={toggleFullscreen}
        onToggleParticipants={() => setShowParticipantsPanel((prev) => !prev)}
        onLeave={handleLeaveWithFlush}
        onToggleVideoSettings={toggleVideoSettings}
      />
    </div>
  );
};

const TelehealthHeader = ({
  sessionName,
  displayName,
  userMRN,
  linkedPatientMrn,
  networkQuality,
  callDuration
}) => {
  const pmrn = linkedPatientMrn != null ? linkedPatientMrn : userMRN;
  return (
    <header className="h-14 border-b border-gray-200 bg-white flex items-center justify-between px-4 md:px-6 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-teal-500 flex items-center justify-center text-xs font-semibold text-white">
            Dr
          </div>
          <div className="text-xs">
            <p className="text-gray-900 font-medium truncate max-w-[12rem]">{displayName || 'Clinician'}</p>
            {pmrn != null && (
              <p className="text-[11px] text-gray-500">PMRN {pmrn}</p>
            )}
          </div>
        </div>
        <div className="hidden md:flex items-center gap-3 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <span className="font-medium text-gray-700">Session</span>
            <span className="truncate max-w-[8rem]">{sessionName}</span>
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <div className="hidden sm:flex items-center gap-1.5">
          <span className="inline-flex h-7 items-center rounded-full bg-gray-100 px-2.5 text-gray-700 font-medium">
            {callDuration}
          </span>
        </div>
        <NetworkQualityIndicator quality={networkQuality} />
        <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5" title="Powered by Zoom">
          <img src="/Zoom_Logo_Bloom.svg" alt="Zoom" className="h-3 w-3" />
          <span className="text-[10px] text-blue-700 font-medium">Zoom</span>
        </span>
      </div>
    </header>
  );
};

const NetworkQualityIndicator = ({ quality }) => {
  const bars = [1, 2, 3, 4];
  return (
    <div className="inline-flex items-end gap-[1px] rounded-full bg-gray-100 border border-gray-200 px-1.5 py-0.5" title="Network quality">
      {bars.map((bar) => (
        <div
          key={bar}
          className={`w-0.5 rounded-full ${bar <= quality ? 'bg-teal-500' : 'bg-gray-300'}`}
          style={{ height: 3 + bar * 2 }}
        />
      ))}
    </div>
  );
};

const VideoLayout = ({ selfUser, remoteParticipants }) => {
  const hasRemote = remoteParticipants.length > 0;
  const users = hasRemote
    ? [...remoteParticipants, ...(selfUser ? [selfUser] : [])]
    : selfUser
      ? [selfUser]
      : [];

  const total = users.length;
  const gridColumns = total <= 1 ? '1fr' : 'repeat(2, 1fr)';

  return (
    <div className="flex-1 min-h-0 relative bg-black overflow-hidden">
      {users.length > 0 ? (
        <div
          className="w-full h-full"
          style={{
            display: 'grid',
            gridTemplateColumns: gridColumns,
            gridAutoRows: '1fr',
            width: '100%',
            height: '100%',
            background: '#0f172a',
            overflow: 'hidden',
            gridAutoFlow: 'row',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {users.map((u) => (
            <div key={u.userId} className="relative min-w-0 min-h-0 overflow-hidden">
              <VideoPlayerContainerComponent style={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0 }}>
                <VideoPlayerComponent user={u} />
              </VideoPlayerContainerComponent>

              <div className="absolute left-3 bottom-3 z-10 pointer-events-none">
                <span className="inline-flex items-center gap-2 rounded-full bg-black/60 px-2.5 py-1 text-xs text-white">
                  <span className="truncate max-w-[12rem]">{u.userId === selfUser?.userId ? 'You' : (u.displayName || 'Participant')}</span>
                  {u.bVideoOn ? (
                    <span className="inline-flex items-center justify-center rounded-full bg-teal-500/20 text-teal-200 px-1.5 py-0.5">
                      <Video className="w-3 h-3" />
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center rounded-full bg-gray-500/30 text-gray-200 px-1.5 py-0.5">
                      <VideoOff className="w-3 h-3" />
                    </span>
                  )}
                  {u.muted ? (
                    <span className="inline-flex items-center justify-center rounded-full bg-red-500/20 text-red-200 px-1.5 py-0.5">
                      <MicOff className="w-3 h-3" />
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center rounded-full bg-teal-500/20 text-teal-200 px-1.5 py-0.5">
                      <Mic className="w-3 h-3" />
                    </span>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
          <div className="rounded-xl bg-white/95 border border-gray-200 px-6 py-5 shadow-lg max-w-sm text-center">
            <p className="text-sm font-medium text-gray-900">Waiting for patient to connect…</p>
            <p className="text-xs text-gray-600 mt-1">You'll see the patient when they join.</p>
          </div>
        </div>
      )}
    </div>
  );
};

const ControlBar = ({
  show,
  isVideoOn,
  isAudioMuted,
  isFullscreen,
  onToggleVideo,
  onToggleAudio,
  onToggleFullscreen,
  onToggleParticipants,
  onLeave,
  onToggleVideoSettings
}) => {
  if (!show) return null;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-16 md:bottom-14 flex justify-center z-50"
    >
      <div className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-3 py-2 shadow-lg">
        <ControlButton
          active={!isAudioMuted}
          variant={isAudioMuted ? 'danger' : 'primary'}
          label={isAudioMuted ? 'Unmute' : 'Mute'}
          tooltip="Microphone (M)"
          icon={isAudioMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          onClick={onToggleAudio}
        />
        <ControlButton
          active={isVideoOn}
          variant={isVideoOn ? 'primary' : 'danger'}
          label={isVideoOn ? 'Video off' : 'Video on'}
          tooltip="Camera (V)"
          icon={isVideoOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
          onClick={onToggleVideo}
        />
        <div className="w-px h-6 bg-gray-200" />
        <ControlButton
          variant="secondary"
          label="Settings"
          tooltip="Session & video settings"
          icon={<Settings className="w-4 h-4" />}
          onClick={onToggleVideoSettings}
        />
        <div className="w-px h-6 bg-gray-200" />
        <ControlButton variant="secondary" label="Participants" tooltip="Participants (P)" icon={<Users className="w-4 h-4" />} onClick={onToggleParticipants} />
        <ControlButton active={isFullscreen} variant="secondary" label={isFullscreen ? 'Exit full' : 'Fullscreen'} tooltip="Fullscreen (F)" icon={<Maximize className="w-4 h-4" />} onClick={onToggleFullscreen} />
        <div className="w-px h-6 bg-gray-200" />
        <ControlButton variant="danger" label="Leave call" tooltip="Leave (Q)" icon={<PhoneOff className="w-4 h-4" />} onClick={onLeave} />
      </div>
    </div>
  );
};

const ControlButton = ({ variant, label, icon, tooltip, onClick }) => {
  const base = 'inline-flex items-center justify-center rounded-full text-xs font-medium transition-all px-3 py-2 gap-1.5';
  const variants = {
    primary: 'bg-teal-500 text-white hover:bg-teal-600',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200',
    danger: 'bg-red-500 text-white hover:bg-red-600'
  };
  return (
    <button type="button" onClick={onClick} className={`${base} ${variants[variant]}`} title={tooltip}>
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
};

const ParticipantsPanel = ({ open, participants, selfUser, raisedHands, networkQuality, onToggleMuteParticipant }) => {
  if (!open) return null;

  return (
    <aside className="hidden lg:flex w-56 flex-col border-l border-gray-200 bg-white shadow-sm">
      <div className="h-11 flex items-center px-3 border-b border-gray-200 text-sm font-medium text-gray-900">
        Participants ({participants.length})
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5">
        {participants.map((user) => {
          const isSelf = user.userId === selfUser?.userId;
          const hasHandRaised = !!raisedHands[user.userId];
          const videoOn = !!user.bVideoOn;
          const micOff = user.muted === true;
          return (
            <div
              key={user.userId}
              className="flex items-center justify-between rounded-lg px-2.5 py-2 text-sm bg-gray-50 border border-gray-100 hover:bg-gray-100"
            >
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-medium text-sm flex-shrink-0">
                  {(user.displayName || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="flex flex-col overflow-hidden min-w-0">
                  <span className="truncate font-medium text-gray-900">{user.displayName || (isSelf ? 'You' : 'Participant')}</span>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    {isSelf && <span>Doctor</span>}
                    {hasHandRaised && <span className="text-amber-600">Hand raised</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${
                    videoOn ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-400'
                  }`}
                  title={videoOn ? 'Video on' : 'Video off'}
                >
                  {videoOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                </span>
                {isSelf ? (
                  <span
                    className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${
                      micOff ? 'bg-red-50 text-red-700' : 'bg-teal-50 text-teal-700'
                    }`}
                    title={micOff ? 'Mic off' : 'Mic on'}
                  >
                    {micOff ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onToggleMuteParticipant(user.userId)}
                    className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${
                      micOff ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                    }`}
                    title={micOff ? 'Unmute participant' : 'Mute participant'}
                  >
                    {micOff ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default OnlineConsultationPage;
